import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import PageTransition from "../components/PageTransition";
import { LazyBackgroundImage } from "../../shared/components";

interface FlowerProfile {
  name: string;
  slug?: string;
  emoji: string;
  title: string;
  description: string;
  botany_description: string;
  adaptability_insight: string;
  personality_strengths: string;
  color: string;
  bg_gradient: string;
}

interface FlowerSummary {
  name: string;
  slug: string;
  emoji: string;
  title: string;
  color: string;
}

interface QuizResult {
  score: number;
  flower: {
    name: string;
    emoji: string;
    slug: string;
    color: string;
  };
}

const FLOWER_COLORS: Record<string, string> = {
  red: "#EF4444",
  rose: "#F43F5E",
  yellow: "#EAB308",
  orange: "#F97316",
  purple: "#8B5CF6",
  pink: "#EC4899",
  amber: "#F59E0B",
  lime: "#84CC16",
};

// Strong gradients for the main flower card
const FLOWER_GRADIENTS: Record<string, string> = {
  red: "linear-gradient(135deg, #FEE2E2, #FECDD3)",
  rose: "linear-gradient(135deg, #FFE4E6, #FEE2E2)",
  yellow: "linear-gradient(135deg, #FEF9C3, #FDE68A)",
  orange: "linear-gradient(135deg, #FFEDD5, #FEE2E2)",
  purple: "linear-gradient(135deg, #EDE9FE, #E0E7FF)",
  pink: "linear-gradient(135deg, #FCE7F3, #FAE8FF)",
  amber: "linear-gradient(135deg, #FEF3C7, #FEF9C3)",
  lime: "linear-gradient(135deg, #ECFCCB, #D9F99D)",
};

// Lighter tints for content cards — 3 variants per flower for visual variety
const CARD_GRADIENTS: Record<string, [string, string, string]> = {
  red:    ["linear-gradient(135deg, #FEE2E2, #FFF1F2)", "linear-gradient(135deg, #FFF1F2, #FFE4E6)", "linear-gradient(135deg, #FFE4E6, #FEE2E2)"],
  rose:   ["linear-gradient(135deg, #FFE4E6, #FFF1F2)", "linear-gradient(135deg, #FFF1F2, #FCE7F3)", "linear-gradient(135deg, #FCE7F3, #FFE4E6)"],
  yellow: ["linear-gradient(135deg, #FEF9C3, #FFFBEB)", "linear-gradient(135deg, #FFFBEB, #FEF3C7)", "linear-gradient(135deg, #FEF3C7, #FEF9C3)"],
  orange: ["linear-gradient(135deg, #FFEDD5, #FFF7ED)", "linear-gradient(135deg, #FFF7ED, #FEF3C7)", "linear-gradient(135deg, #FEF3C7, #FFEDD5)"],
  purple: ["linear-gradient(135deg, #EDE9FE, #F5F3FF)", "linear-gradient(135deg, #F5F3FF, #E0E7FF)", "linear-gradient(135deg, #E0E7FF, #EDE9FE)"],
  pink:   ["linear-gradient(135deg, #FCE7F3, #FDF2F8)", "linear-gradient(135deg, #FDF2F8, #FAE8FF)", "linear-gradient(135deg, #FAE8FF, #FCE7F3)"],
  amber:  ["linear-gradient(135deg, #FEF3C7, #FFFBEB)", "linear-gradient(135deg, #FFFBEB, #FEF9C3)", "linear-gradient(135deg, #FEF9C3, #FEF3C7)"],
  lime:   ["linear-gradient(135deg, #ECFCCB, #F7FEE7)", "linear-gradient(135deg, #F7FEE7, #D9F99D)", "linear-gradient(135deg, #D9F99D, #ECFCCB)"],
};

// Fade-out gradient for collapsed sections, matching card background
const FADE_COLORS: Record<string, string> = {
  red: "#FFF1F2", rose: "#FFF1F2", yellow: "#FFFBEB", orange: "#FFF7ED",
  purple: "#F5F3FF", pink: "#FDF2F8", amber: "#FFFBEB", lime: "#F7FEE7",
};

const Results: React.FC = () => {
  const { uuid } = useParams<{ uuid: string }>();
  const [searchParams] = useSearchParams();
  const [profile, setProfile] = useState<FlowerProfile | null>(null);
  const [quizResults, setQuizResults] = useState<QuizResult[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [botanyExpanded, setBotanyExpanded] = useState(false);
  const [personalityExpanded, setPersonalityExpanded] = useState(false);
  const [allFlowers, setAllFlowers] = useState<FlowerSummary[]>([]);
  const [currentSlug, setCurrentSlug] = useState<string | null>(null);

  useEffect(() => {
    if (uuid) {
      fetchByUuid(uuid);
    } else {
      const slug =
        searchParams.get("slug") ||
        searchParams.get("flower") ||
        "sunflower";
      setCurrentSlug(slug);
      fetchBySlug(slug);
    }
    fetchAllFlowers();
  }, [uuid, searchParams]);

  const fetchByUuid = async (id: string) => {
    try {
      const response = await fetch(`/api/quiz/results/${id}`);
      if (!response.ok) throw new Error("Failed to load results");
      const data = await response.json();
      setQuizResults(data.results);
      const topFlower = data.top_flower?.flower;
      if (topFlower) {
        setProfile(topFlower);
        setCurrentSlug(topFlower.slug || null);
      } else {
        setError("Result not found");
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load results"
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchBySlug = async (slug: string) => {
    try {
      const response = await fetch(`/api/flowers/${slug}`);
      if (!response.ok) throw new Error("Failed to load flower profile");
      const data = await response.json();
      setProfile(data.flower);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load results"
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchAllFlowers = async () => {
    try {
      const response = await fetch("/api/flowers");
      if (!response.ok) return;
      const data = await response.json();
      setAllFlowers(data.flowers);
    } catch {
      // Non-critical — grid just won't show
    }
  };

  const renderParagraphs = (text: string) => {
    if (!text) return null;
    return text.split("\n\n").map((paragraph, index) => (
      <p
        key={index}
        className="text-lg text-gray-600 dark:text-gray-300 leading-relaxed mb-4 last:mb-0"
      >
        {paragraph}
      </p>
    ));
  };

  const getChartOptions = (): { options: ApexOptions; series: { data: number[] }[] } | null => {
    if (!quizResults || quizResults.length === 0) return null;

    const sorted = [...quizResults].sort((a, b) => b.score - a.score);
    const categories = sorted.map((r) => `${r.flower.emoji} ${r.flower.name}`);
    const scores = sorted.map((r) => Math.round(r.score * 100) / 100);
    const colors = sorted.map(
      (r) => FLOWER_COLORS[r.flower.color] || "#8B5CF6"
    );

    return {
      options: {
        chart: {
          type: "bar" as const,
          toolbar: { show: false },
          background: "transparent",
        },
        plotOptions: {
          bar: {
            horizontal: true,
            borderRadius: 6,
            distributed: true,
            barHeight: "70%",
          },
        },
        colors,
        dataLabels: {
          enabled: true,
          formatter: (val: number) => val.toFixed(2),
          style: { fontSize: "13px", fontWeight: 600, colors: ["#fff"] },
        },
        xaxis: {
          categories,
          max: 3,
          labels: {
            style: { colors: "#9CA3AF", fontSize: "12px" },
          },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: {
          labels: {
            style: { colors: "#9CA3AF", fontSize: "14px", fontWeight: 600 },
          },
        },
        grid: { borderColor: "#E5E7EB", strokeDashArray: 4 },
        legend: { show: false },
        tooltip: {
          enabled: true,
          y: {
            formatter: (val: number) => `${val.toFixed(2)} / 3.00`,
          },
        },
      },
      series: [{ data: scores }],
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-brand-50 to-yellow-25 dark:from-gray-900 dark:to-gray-800">
        <p className="text-gray-600 dark:text-gray-400">
          Loading your results...
        </p>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-brand-50 to-yellow-25 dark:from-gray-900 dark:to-gray-800">
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400 mb-4">
            {error || "Profile not found"}
          </p>
          <Link
            to="/quiz"
            className="text-brand-600 dark:text-brand-400 hover:underline"
          >
            Take the quiz
          </Link>
        </div>
      </div>
    );
  }

  const chartData = getChartOptions();

  return (
    <PageTransition>
      <div className="relative min-h-screen bg-gradient-to-b from-brand-50 to-yellow-25 dark:from-gray-900 dark:to-gray-800 py-12 overflow-hidden">
        {/* Decorative Vines */}
        <LazyBackgroundImage
          webpSrc="/images/optimized/vines_3.webp"
          fallbackSrc="/images/vines_3.png"
          className="hidden lg:block absolute inset-y-0 left-0 w-1/2 bg-contain bg-left bg-no-repeat opacity-50"
          rootMargin="200px"
        />
        <LazyBackgroundImage
          webpSrc="/images/optimized/vines_4.webp"
          fallbackSrc="/images/vines_4.png"
          className="hidden lg:block absolute inset-y-0 right-0 w-1/2 bg-contain bg-right bg-no-repeat opacity-50"
          rootMargin="200px"
        />

        <div className="relative z-10 mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          {/* Result Header */}
          <div className="text-center mb-12 animate-slide-up">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Your Flower Personality
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-400">
              Based on your adaptability traits, you are...
            </p>
          </div>

          {/* 1. Flower Card */}
          <div
            className="rounded-3xl shadow-2xl p-8 sm:p-12 mb-8 animate-slide-up animation-delay-100 animation-fill-both"
            style={{ background: FLOWER_GRADIENTS[profile.color] || FLOWER_GRADIENTS.purple }}
          >
            <div className="text-center">
              <div className="text-8xl mb-6">{profile.emoji}</div>
              <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-3">
                {profile.name}
              </h2>
              <p className="text-2xl text-gray-700 dark:text-gray-200 font-semibold">
                {profile.title}
              </p>
            </div>
          </div>

          {/* 2. Blossom Score Bar Graph */}
          {chartData && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 sm:p-8 mb-6 animate-slide-up animation-delay-150 animation-fill-both">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Your Blossom Scores
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                How you scored across all eight blossom personalities
              </p>
              <Chart
                options={chartData.options}
                series={chartData.series}
                type="bar"
                height={320}
              />
            </div>
          )}

          {/* 3. Adaptability Trait snapshot */}
          {profile.adaptability_insight && (
            <div
              className="rounded-2xl shadow-lg p-8 sm:p-10 mb-6 animate-slide-up animation-delay-150 animation-fill-both dark:bg-gray-800"
              style={{ background: (CARD_GRADIENTS[profile.color] || CARD_GRADIENTS.purple)[0] }}
            >
              <div className="flex items-center gap-3 mb-4">
                <span className="text-3xl">🌿</span>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Adaptability Trait
                </h3>
              </div>
              <p className="text-lg text-gray-600 dark:text-gray-300 leading-relaxed">
                {profile.adaptability_insight}
              </p>
            </div>
          )}

          {/* 4. Personality Strengths snapshot */}
          {profile.personality_strengths && (
            <div
              className="rounded-2xl shadow-lg p-8 sm:p-10 mb-6 animate-slide-up animation-delay-200 animation-fill-both dark:bg-gray-800"
              style={{ background: (CARD_GRADIENTS[profile.color] || CARD_GRADIENTS.purple)[1] }}
            >
              <div className="flex items-center gap-3 mb-4">
                <span className="text-3xl">✨</span>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Personality Strengths
                </h3>
              </div>
              <p className="text-lg text-gray-600 dark:text-gray-300 leading-relaxed">
                {profile.personality_strengths}
              </p>
            </div>
          )}

          {/* 5/6. Botany Description (collapsible) */}
          {profile.botany_description && (
            <div
              className="rounded-2xl shadow-lg p-8 sm:p-10 mb-6 animate-slide-up animation-delay-200 animation-fill-both dark:bg-gray-800"
              style={{ background: (CARD_GRADIENTS[profile.color] || CARD_GRADIENTS.purple)[1] }}
            >
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Botany of a {profile.name}
              </h3>
              <div className="relative">
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    botanyExpanded ? "max-h-[2000px]" : "max-h-24"
                  }`}
                >
                  {renderParagraphs(profile.botany_description)}
                </div>
                {!botanyExpanded && (
                  <div
                    className="absolute bottom-0 left-0 right-0 h-16 pointer-events-none"
                    style={{ background: `linear-gradient(to top, ${FADE_COLORS[profile.color] || FADE_COLORS.purple}, transparent)` }}
                  />
                )}
              </div>
              <button
                onClick={() => setBotanyExpanded(!botanyExpanded)}
                className="mt-3 text-brand-600 dark:text-brand-400 font-medium hover:text-brand-500 transition-colors"
              >
                {botanyExpanded ? "Read Less \u2191" : "Read More \u2193"}
              </button>
            </div>
          )}

          {/* 7. Personality Description (collapsible) */}
          <div
            className="rounded-2xl shadow-lg p-8 sm:p-10 mb-8 animate-slide-up animation-delay-300 animation-fill-both dark:bg-gray-800"
            style={{ background: (CARD_GRADIENTS[profile.color] || CARD_GRADIENTS.purple)[2] }}
          >
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Personality of a {profile.name}
            </h3>
            <div className="relative">
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  personalityExpanded ? "max-h-[2000px]" : "max-h-24"
                }`}
              >
                {renderParagraphs(profile.description)}
              </div>
              {!personalityExpanded && (
                <div
                  className="absolute bottom-0 left-0 right-0 h-16 pointer-events-none"
                  style={{ background: `linear-gradient(to top, ${FADE_COLORS[profile.color] || FADE_COLORS.purple}, transparent)` }}
                />
              )}
            </div>
            <button
              onClick={() => setPersonalityExpanded(!personalityExpanded)}
              className="mt-3 text-brand-600 dark:text-brand-400 font-medium hover:text-brand-500 transition-colors"
            >
              {personalityExpanded ? "Read Less \u2191" : "Read More \u2193"}
            </button>
          </div>

          {/* 8. Explore All Blossoms */}
          {allFlowers.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 sm:p-10 mb-8 animate-slide-up animation-delay-400 animation-fill-both">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2 text-center">
                Explore All Blossoms
              </h3>
              <p className="text-gray-500 dark:text-gray-400 text-center mb-6">
                Discover every flower personality
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {allFlowers.map((flower) => {
                  const isCurrent = flower.slug === currentSlug;
                  return (
                    <Link
                      key={flower.slug}
                      to={`/results?slug=${flower.slug}`}
                      className={`rounded-xl p-4 text-center transition-all hover:scale-105 hover:shadow-md ${
                        isCurrent ? "ring-2 ring-brand-500 shadow-md" : ""
                      }`}
                      style={{ background: FLOWER_GRADIENTS[flower.color] || FLOWER_GRADIENTS.purple }}
                    >
                      <div className="text-4xl mb-2">{flower.emoji}</div>
                      <div className="font-semibold text-gray-900 text-sm">{flower.name}</div>
                      <div className="text-xs italic text-gray-600 mt-0.5">{flower.title}</div>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {/* 9. CTA Buttons */}
          <div className="bg-gradient-to-r from-brand-500 to-lavender-400 dark:bg-brand-700 rounded-2xl shadow-lg p-8 text-center">
            <h3 className="text-2xl font-bold text-white mb-4">
              Want to Learn More?
            </h3>
            <p className="text-white/90 mb-6">
              Discover deeper insights about your adaptability style and how
              to cultivate your natural brilliance in "The Brilliance of Blossoms"
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/about"
                className="inline-block rounded-md bg-white px-6 py-3 text-base font-semibold text-brand-600 shadow-sm hover:bg-gray-50 transition-all"
              >
                About the Book
              </Link>
              <Link
                to="/quiz"
                className="inline-block rounded-md border-2 border-white px-6 py-3 text-base font-semibold text-white hover:bg-white hover:text-brand-600 transition-all"
              >
                Retake Quiz
              </Link>
            </div>
          </div>

          {/* Share Section */}
          <div className="mt-8 text-center">
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Share your result with friends!
            </p>
            <div className="flex justify-center gap-4">
              <button
                onClick={() => {
                  const url = window.location.href;
                  navigator.clipboard.writeText(url);
                  alert("Link copied to clipboard!");
                }}
                className="px-6 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-all"
              >
                Copy Link
              </button>
            </div>
          </div>
        </div>
      </div>
    </PageTransition>
  );
};

export default Results;
